// The stub editor's rules, kept out of the component so they can be tested.
//
// These mirror MockEndpointRules on the backend. The backend is the authority —
// it refuses what it will not serve regardless of what this says — but a limit
// the screen only learns by being rejected is a limit somebody hits with a
// half-written body and no idea which field was wrong.

/**
 * The size the backend will measure, which is BYTES and not characters.
 *
 * A JSON body full of names in a non-Latin script is comfortably under a
 * 64,000-character limit and comfortably over a 64KB one. Counting characters
 * here would let the editor say "fine" and the save say "too big".
 */
export const byteLength = (s: string): number =>
  typeof TextEncoder !== 'undefined'
    ? new TextEncoder().encode(s || '').length
    // jsdom and older Safari. encodeURIComponent escapes every non-ASCII byte
    // as %XX, so counting the escapes counts the bytes.
    : encodeURIComponent(s || '').replace(/%[0-9A-F]{2}/gi, 'x').length;

/** The media type alone, lowercased, with any charset dropped — as the backend stores it. */
export const baseType = (contentType: string): string =>
  (contentType || '').trim().toLowerCase().split(';')[0].trim();

/** Whether this type is one the body editor should offer to pretty-print. */
export const isJsonType = (contentType: string): boolean =>
  baseType(contentType) === 'application/json';

export const isXmlType = (contentType: string): boolean =>
  ['application/xml', 'text/xml'].includes(baseType(contentType));

export interface BodyProblem {
  /** 'error' blocks the save; 'warn' is worth saying but not worth refusing. */
  level: 'error' | 'warn';
  message: string;
}

/**
 * What is wrong with this body, if anything.
 *
 * Invalid JSON is a WARNING, not an error. A stub whose whole purpose is to
 * make a caller handle a malformed response is a legitimate thing to build, and
 * refusing to save it would remove a real test case. Over the size cap IS an
 * error, because the backend will refuse it anyway and saying so here is the
 * only way the person finds out before losing the edit.
 */
export const bodyProblem = (
  body: string,
  contentType: string,
  maxBytes: number,
): BodyProblem | null => {
  const bytes = byteLength(body);
  if (bytes > maxBytes) {
    return {
      level: 'error',
      message: `This body is ${bytes.toLocaleString()} bytes and the limit is ${maxBytes.toLocaleString()}.`,
    };
  }
  if (isJsonType(contentType) && (body || '').trim()) {
    try {
      JSON.parse(body);
    } catch (e: any) {
      return {
        level: 'warn',
        message: `This is not valid JSON (${e.message}). It will still be served exactly as written — `
          + 'which is what you want if you are testing how a caller handles a bad response.',
      };
    }
  }
  return null;
};

/**
 * The body pretty-printed, or unchanged when it cannot be.
 *
 * Never throws and never empties the box: a formatter that clears somebody's
 * work because it could not parse it is worse than one that does nothing.
 */
export const formatBody = (body: string, contentType: string): string => {
  if (!isJsonType(contentType) || !(body || '').trim()) return body;
  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    return body;
  }
};

/** Whether the status reads as a success to the caller, for colouring the badge. */
export const statusTone = (status: number): 'ok' | 'warn' | 'bad' => {
  if (status >= 200 && status < 300) return 'ok';
  if (status >= 300 && status < 400) return 'warn';
  return 'bad';
};

/**
 * A ready-to-paste curl for this stub.
 *
 * GET when the stub captures nothing to speak of, POST otherwise — the common
 * reason to build one of these is a webhook, and a webhook is a POST.
 */
export const curlFor = (url: string | null | undefined, contentType: string): string => {
  if (!url) return '';
  if (baseType(contentType) === 'application/json') {
    return `curl -i -X POST '${url}' \\\n  -H 'Content-Type: application/json' \\\n  -d '{"hello":"world"}'`;
  }
  return `curl -i '${url}'`;
};

/**
 * How a hit's headers should be shown.
 *
 * The backend stores them as a JSON object string. A row that cannot be parsed
 * is shown as-is rather than dropped — it is evidence about a caller, and the
 * inspector exists precisely for the calls that are malformed.
 */
export const parseHeaders = (headers: string | null | undefined): [string, string][] => {
  if (!headers) return [];
  try {
    const parsed = JSON.parse(headers);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return Object.entries(parsed).map(([k, v]) => [k, String(v)]);
    }
    return [['(unparseable)', headers]];
  } catch {
    return [['(unparseable)', headers]];
  }
};

/** Bytes as something a person reads at a glance. */
export const humanBytes = (n: number | undefined | null): string => {
  const b = n || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};
